import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Award,
  ChevronLeft,
  ChevronRight,
  BookOpen,
  Target,
  Bell,
  Flame,
  Play,
  XCircle,
} from 'lucide-react';
import { Card, CardHeader, Badge, StatCard, Button, Skeleton, SkeletonCard } from '@azyntor-labs/nexus-ui';
import { dashboardApi, StudentExamResult, UpcomingExam } from '@/api/endpoints';
import { Link } from 'react-router-dom';

// Loading skeleton for the dashboard
const DashboardSkeleton = () => (
  <div className="space-y-6 animate-in fade-in duration-300">
    {/* Header skeleton */}
    <div className="flex items-center justify-between">
      <div>
        <Skeleton width={250} height={32} className="rounded-lg" />
        <Skeleton width={200} height={18} className="mt-2 rounded-lg" />
      </div>
    </div>

    {/* Stats Grid skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="glass-card p-5">
          <div className="flex items-center justify-between mb-3">
            <Skeleton width={100} height={14} className="rounded" />
            <Skeleton variant="circular" width={40} height={40} />
          </div>
          <Skeleton width={80} height={28} className="rounded-lg" />
          <Skeleton width={120} height={12} className="mt-2 rounded" />
        </div>
      ))}
    </div>

    {/* Secondary Stats skeleton */}
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="glass-card p-4 border-l-2 border-white/10">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton width={80} height={14} className="rounded" />
              <Skeleton width={60} height={24} className="rounded-lg" />
            </div>
            <Skeleton variant="rounded" width={40} height={40} />
          </div>
        </div>
      ))}
    </div>

    {/* Exam cards skeleton */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {[1, 2].map((i) => (
        <div key={i} className="glass-card p-6">
          <div className="flex items-center justify-between mb-4">
            <Skeleton width={150} height={20} className="rounded-lg" />
            <Skeleton width={80} height={24} className="rounded-full" />
          </div>
          <div className="space-y-3">
            {[1, 2, 3].map((j) => (
              <SkeletonCard key={j} />
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);
import { format, formatDistanceToNow } from 'date-fns';
import { formatExamDateShort } from '@/utils';
import { useExamAuth } from '@/hooks/useExamAuth';

type ExamFilter = 'active' | 'recent' | 'upcoming' | 'all';

export const StudentDashboard = () => {
  const { user } = useExamAuth();
  const [examFilter, setExamFilter] = useState<ExamFilter>('active');
  const [examPage, setExamPage] = useState(1);
  const examsPerPage = 5;

  const { data: dashboardResponse, isLoading: isLoadingDashboard, error: dashboardError } = useQuery({
    queryKey: ['student-dashboard'],
    queryFn: () => dashboardApi.getStudentDashboard(),
  });

  const dashboard = dashboardResponse?.data?.data;
  const summary = dashboard?.summary;
  const recentExams = dashboard?.recent_exams || [];
  const upcomingExams = dashboard?.upcoming_exams || [];
  const performanceTrend = dashboard?.performance_trend || [];
  const subjectPerformance = dashboard?.subject_performance || [];
  const achievements = dashboard?.achievements || [];
  const notifications = dashboard?.notifications || [];

  // Classify upcoming exams into active (happening now) and future
  const now = new Date();
  const activeExams = upcomingExams.filter(exam => {
    const startTime = new Date(exam.start_time);
    const endTime = new Date(exam.end_time);
    return now >= startTime && now <= endTime;
  });
  const futureExams = upcomingExams.filter(exam => {
    const startTime = new Date(exam.start_time);
    return now < startTime;
  });

  const allExams = [
    ...activeExams.map(e => ({ ...e, type: 'active' as const })),
    ...recentExams.map(e => ({ ...e, type: 'recent' as const })),
    ...futureExams.map(e => ({ ...e, type: 'upcoming' as const }))
  ];

  let filteredExams: (StudentExamResult | UpcomingExam)[] = [];
  switch (examFilter) {
    case 'active':
      filteredExams = activeExams;
      break;
    case 'recent':
      filteredExams = recentExams;
      break;
    case 'upcoming':
      filteredExams = futureExams;
      break;
    case 'all':
      filteredExams = [...activeExams, ...recentExams, ...futureExams];
      break;
  }

  const totalPages = Math.ceil(filteredExams.length / examsPerPage);
  const paginatedExams = filteredExams.slice(
    (examPage - 1) * examsPerPage,
    examPage * examsPerPage
  );

  if (isLoadingDashboard) {
    return <DashboardSkeleton />;
  }

  if (dashboardError) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-white">Welcome, {user?.first_name}!</h1>
          <p className="text-white/50 mt-1">Track your exam progress and performance</p>
        </div>
        <Card>
          <div className="p-8 text-center">
            <AlertCircle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-white mb-2">Unable to load dashboard</h3>
            <p className="text-white/60 mb-4">We're having trouble loading your dashboard data. Please try again later.</p>
            <Button onClick={() => window.location.reload()}>Refresh Page</Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">Welcome, {user?.first_name}!</h1>
          <p className="text-white/50 mt-1">Track your exam progress and performance</p>
        </div>
        {summary?.current_streak && summary.current_streak > 0 && (
          <div className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-orange-500/15 to-amber-500/15 border border-orange-500/30 rounded-lg">
            <Flame className="w-5 h-5 text-orange-400" />
            <span className="font-semibold text-orange-300">{summary.current_streak} day streak!</span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Exams Taken"
          value={summary?.total_exams_taken || 0}
          icon={<BookOpen />}
          color="teal"
          subtitle="Total completed"
        />
        <StatCard
          title="Pass Rate"
          value={`${(summary?.pass_rate || 0).toFixed(1)}%`}
          icon={<Target />}
          color="green"
        />
        <StatCard
          title="Average Score"
          value={`${(summary?.average_score || 0).toFixed(1)}%`}
          icon={<TrendingUp />}
          color="teal"
        />
        <StatCard
          title="Exams Passed"
          value={summary?.total_exams_passed || 0}
          icon={<Award />}
          color="orange"
          subtitle={`of ${summary?.total_exams_taken || 0} taken`}
        />
      </div>

      {/* Secondary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="glass-card p-4 border-l-2 border-emerald-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-emerald-400/80 font-medium">Highest Score</p>
              <p className="text-2xl font-bold text-white">{(summary?.highest_score || 0).toFixed(1)}%</p>
            </div>
            <div className="w-10 h-10 bg-emerald-500/15 rounded-lg flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
            </div>
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-red-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-red-400/80 font-medium">Exams Failed</p>
              <p className="text-2xl font-bold text-white">{summary?.total_exams_failed || 0}</p>
            </div>
            <div className="w-10 h-10 bg-red-500/15 rounded-lg flex items-center justify-center">
              <XCircle className="w-5 h-5 text-red-400" />
            </div>
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-blue-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-blue-400/80 font-medium">Time Spent</p>
              <p className="text-2xl font-bold text-white">{(summary?.total_time_spent_minutes || 0) < 60 ? `${Math.round(summary?.total_time_spent_minutes || 0)}m` : `${Math.round((summary?.total_time_spent_minutes || 0) / 60)}h`}</p>
            </div>
            <div className="w-10 h-10 bg-blue-500/15 rounded-lg flex items-center justify-center">
              <Clock className="w-5 h-5 text-blue-400" />
            </div>
          </div>
        </div>
        <div className="glass-card p-4 border-l-2 border-purple-500/50">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-purple-400/80 font-medium">Upcoming</p>
              <p className="text-2xl font-bold text-white">{upcomingExams.length}</p>
            </div>
            <div className="w-10 h-10 bg-purple-500/15 rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-purple-400" />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exams with Filter and Pagination */}
        <Card>
          <div className="p-6 border-b border-white/[0.06]">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-white">My Exams</h2>
                <select
                  value={examFilter}
                  onChange={(e) => {
                    setExamFilter(e.target.value as ExamFilter);
                    setExamPage(1);
                  }}
                  className="mt-2 text-sm bg-white/5 border border-white/[0.06] rounded-lg px-3 py-1.5 text-white/80 focus:ring-2 focus:ring-teal-500/20 focus:border-teal-500/50 outline-none"
                >
                  <option value="active" className="bg-[#12121a]">Active Now ({activeExams.length})</option>
                  <option value="recent" className="bg-[#12121a]">Completed ({recentExams.length})</option>
                  <option value="upcoming" className="bg-[#12121a]">Upcoming ({futureExams.length})</option>
                  <option value="all" className="bg-[#12121a]">All ({allExams.length})</option>
                </select>
              </div>
            </div>
          </div>
          <div className="p-6">
            {paginatedExams.length > 0 ? (
              <div className="space-y-3">
                {paginatedExams.map((exam) => {
                  const isRecent = 'attempt_id' in exam;
                  const recentExam = exam as StudentExamResult;
                  const upcomingExam = exam as UpcomingExam;

                  // Check if exam is currently active
                  const isActive = !isRecent && upcomingExam.start_time && upcomingExam.end_time &&
                    now >= new Date(upcomingExam.start_time) &&
                    now <= new Date(upcomingExam.end_time);

                  return (
                    <div
                      key={isRecent ? recentExam.attempt_id : upcomingExam.exam_id}
                      className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-white/[0.04] hover:border-white/[0.1] transition-all"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-white mb-1">
                            {isRecent ? recentExam.exam_title : (upcomingExam.exam_title || upcomingExam.title)}
                          </h3>
                          <p className="text-sm text-white/50">
                            {isRecent ? recentExam.subject : upcomingExam.subject}
                          </p>
                        </div>
                        <Badge
                          variant={isRecent ? (recentExam.passed ? 'success' : 'danger') : (isActive ? 'info' : 'warning')}
                          size="sm"
                        >
                          {isRecent ? (recentExam.passed ? 'Passed' : 'Failed') : (isActive ? 'Active' : 'Upcoming')}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center space-x-4 text-sm text-white/50">
                          {isRecent ? (
                            <>
                              <div className="flex items-center">
                                <Target className="w-4 h-4 mr-1" />
                                {recentExam.percentage.toFixed(1)}%
                              </div>
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {Math.round((recentExam.time_taken || 0) / 60) || recentExam.time_spent_minutes} min
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                {formatExamDateShort(upcomingExam.start_time)}
                              </div>
                              <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                {upcomingExam.duration || upcomingExam.duration_minutes} min
                              </div>
                            </>
                          )}
                        </div>
                        {isRecent ? (
                          <Link to={`/student/results`}>
                            <Button size="sm" variant="secondary">View Details</Button>
                          </Link>
                        ) : isActive ? (
                          <Link to={`/student/exams/${upcomingExam.exam_id}/take`}>
                            <Button size="sm" leftIcon={<Play className="w-3 h-3" />}>
                              {upcomingExam.has_attempted ? 'Retake Exam' : 'Start Exam'}
                            </Button>
                          </Link>
                        ) : (
                          <Link to={`/student/exams/${upcomingExam.exam_id}/take`}>
                            <Button size="sm" variant="secondary">View Details</Button>
                          </Link>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between pt-4 border-t border-white/[0.06]">
                    <button
                      onClick={() => setExamPage(p => Math.max(1, p - 1))}
                      disabled={examPage === 1}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm text-white/60 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      <span>Previous</span>
                    </button>
                    <span className="text-sm text-white/50">
                      Page {examPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setExamPage(p => Math.min(totalPages, p + 1))}
                      disabled={examPage === totalPages}
                      className="flex items-center space-x-1 px-3 py-1.5 text-sm text-white/60 hover:text-white disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <span>Next</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckCircle className="w-12 h-12 text-white/20 mx-auto mb-2" />
                <p className="text-white/40">
                  {examFilter === 'active' && 'No active exams right now'}
                  {examFilter === 'recent' && 'No completed exams yet'}
                  {examFilter === 'upcoming' && 'No upcoming exams scheduled'}
                  {examFilter === 'all' && 'No exams found'}
                </p>
              </div>
            )}
          </div>
        </Card>

        {/* Subject Performance */}
        <Card>
          <CardHeader
            title="Subject Performance"
            subtitle="Your performance by subject"
          />
          {subjectPerformance.length > 0 ? (
            <div className="space-y-4">
              {subjectPerformance.slice(0, 5).map((subject) => (
                <div key={subject.subject} className="p-4 rounded-lg bg-white/[0.02] border border-white/[0.06]">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-white">{subject.subject}</h3>
                    <Badge variant={subject.pass_rate >= 70 ? 'success' : subject.pass_rate >= 50 ? 'warning' : 'danger'} size="sm">
                      {subject.pass_rate.toFixed(0)}% pass rate
                    </Badge>
                  </div>
                  <div className="flex items-center space-x-4 text-sm text-white/50 mb-3">
                    <span>{subject.exams_taken} exams</span>
                    <span>Avg: {subject.average_score.toFixed(1)}%</span>
                    <span>Best: {subject.highest_score.toFixed(1)}%</span>
                  </div>
                  <div className="bg-white/10 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${
                        subject.average_score >= 70 ? 'bg-emerald-500' :
                        subject.average_score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, subject.average_score)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <BookOpen className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">Complete exams to see your subject performance</p>
            </div>
          )}
        </Card>
      </div>

      {/* Achievements & Notifications Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Achievements */}
        <Card>
          <CardHeader
            title="Achievements"
            subtitle="Your earned badges and milestones"
          />
          {achievements.length > 0 ? (
            <div className="grid grid-cols-2 gap-3">
              {achievements.slice(0, 4).map((achievement) => (
                <div
                  key={achievement.id}
                  className="p-3 rounded-lg bg-gradient-to-r from-amber-500/10 to-orange-500/10 border border-amber-500/30"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-2xl">{achievement.icon || '🏆'}</span>
                    <h4 className="font-semibold text-white text-sm">{achievement.title}</h4>
                  </div>
                  <p className="text-xs text-white/60">{achievement.description}</p>
                  <p className="text-xs text-white/40 mt-1">
                    {formatDistanceToNow(new Date(achievement.earned_at), { addSuffix: true })}
                  </p>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Award className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">Complete exams to earn achievements</p>
            </div>
          )}
        </Card>

        {/* Notifications */}
        <Card>
          <CardHeader
            title="Notifications"
            subtitle="Recent updates and alerts"
            action={
              notifications.filter(n => !n.is_read).length > 0 && (
                <Badge variant="danger" size="sm">
                  {notifications.filter(n => !n.is_read).length} new
                </Badge>
              )
            }
          />
          {notifications.length > 0 ? (
            <div className="space-y-3">
              {notifications.slice(0, 4).map((notification) => (
                <div
                  key={notification.id}
                  className={`p-3 rounded-lg border ${
                    notification.is_read
                      ? 'bg-white/[0.02] border-white/[0.06]'
                      : 'bg-blue-500/10 border-blue-500/30'
                  }`}
                >
                  <div className="flex items-start space-x-3">
                    <Bell className={`w-5 h-5 mt-0.5 ${notification.is_read ? 'text-white/40' : 'text-blue-400'}`} />
                    <div className="flex-1">
                      <h4 className="font-semibold text-white text-sm">{notification.title}</h4>
                      <p className="text-xs text-white/60 mt-0.5">{notification.message}</p>
                      <p className="text-xs text-white/40 mt-1">
                        {formatDistanceToNow(new Date(notification.created_at), { addSuffix: true })}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Bell className="w-12 h-12 text-white/20 mx-auto mb-2" />
              <p className="text-white/40">No notifications yet</p>
            </div>
          )}
        </Card>
      </div>

      {/* Performance Trend */}
      {performanceTrend.length > 0 && (
        <Card>
          <CardHeader
            title="Performance Trend"
            subtitle="Your recent exam performance over time"
          />
          <div className="overflow-x-auto">
            <div className="flex items-end space-x-2 min-w-max p-4">
              {performanceTrend.slice(-14).map((day) => {
                const height = Math.max(20, (day.average_score / 100) * 150);
                return (
                  <div key={day.date} className="flex flex-col items-center group">
                    <div className="relative">
                      <div
                        className={`w-8 rounded-t transition-all cursor-pointer hover:opacity-80 ${
                          day.average_score >= 70 ? 'bg-emerald-500' :
                          day.average_score >= 50 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ height: `${height}px` }}
                      />
                      <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-[#12121a] border border-white/[0.06] px-2 py-1 rounded text-xs text-white opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                        {day.average_score.toFixed(1)}%
                      </div>
                    </div>
                    <span className="text-xs text-white/50 mt-1">
                      {format(new Date(day.date), 'dd')}
                    </span>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between px-4 pb-2 text-xs text-white/40">
              <span>{performanceTrend.length > 0 && format(new Date(performanceTrend[Math.max(0, performanceTrend.length - 14)].date), 'MMM dd')}</span>
              <span>{performanceTrend.length > 0 && format(new Date(performanceTrend[performanceTrend.length - 1].date), 'MMM dd')}</span>
            </div>
          </div>
        </Card>
      )}

      {/* Quick Actions */}
      <Card>
        <CardHeader title="Quick Actions" subtitle="Common tasks" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link
            to="/student/results"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-teal-500/10 hover:border-teal-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-teal-500/15 rounded-lg flex items-center justify-center group-hover:bg-teal-500/25 transition-colors">
              <TrendingUp className="w-5 h-5 text-teal-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">View Results</h3>
              <p className="text-sm text-white/40">See all your exam results</p>
            </div>
          </Link>

          {upcomingExams.length > 0 && (
            <Link
              to={`/student/exams/${upcomingExams[0].exam_id}/take`}
              className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-emerald-500/10 hover:border-emerald-500/30 transition-all group"
            >
              <div className="w-10 h-10 bg-emerald-500/15 rounded-lg flex items-center justify-center group-hover:bg-emerald-500/25 transition-colors">
                <Play className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <h3 className="font-medium text-white">Start Next Exam</h3>
                <p className="text-sm text-white/40">{upcomingExams[0].exam_title || upcomingExams[0].title}</p>
              </div>
            </Link>
          )}

          <Link
            to="/profile"
            className="flex items-center space-x-3 p-4 rounded-lg bg-white/[0.02] border border-white/[0.06] hover:bg-purple-500/10 hover:border-purple-500/30 transition-all group"
          >
            <div className="w-10 h-10 bg-purple-500/15 rounded-lg flex items-center justify-center group-hover:bg-purple-500/25 transition-colors">
              <Award className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <h3 className="font-medium text-white">My Profile</h3>
              <p className="text-sm text-white/40">View achievements & stats</p>
            </div>
          </Link>
        </div>
      </Card>
    </div>
  );
};
